import React, { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { UserColumns } from '../columns/UsersColumns';
import { UsersDataGrid } from '../components/UsersDataGrid';
import { useUsers } from '../hooks/useUsers';
import pluralize from 'pluralize';
import { useUserManagementContext } from '../context/UserManagementContext';
import { usePagination } from '../../../shared/hooks/usePagination';
import { GridColumnVisibilityModel } from '@mui/x-data-grid';
import { ApiError } from '../../../shared/api/apiService';
import { CustomErrorPage } from '../../CustomErrorPage';

export default function UsersPage() {
  const navigate = useNavigate();
  const { setIsUserEditDrawerOpen, setCurrentUser } =
    useUserManagementContext();

  const [columnVisibilityModel, setColumnVisibilityModel] =
    useState<GridColumnVisibilityModel>({
      phoneNumber: false,
      companyName: false,
    });

  const {
    pageIndex,
    pageSize,
    paginationModel,
    filterModel,
    sortModel,
    filters,
    quickFilter,
    sortField,
    sortOrder,
    handleFilterModelChange,
    handlePagination,
    handleSortModelChange,
  } = usePagination({ initialSortField: '' });

  const sort = sortField
    ? [
        {
          field: sortField,
          direction: sortOrder.toUpperCase() as 'ASC' | 'DESC',
        },
      ]
    : [];

  const {
    data: usersData,
    isLoading,
    isFetching,
    isError,
    error,
  } = useUsers({
    pageSize,
    page: pageIndex,
    filters,
    search: quickFilter,
    sort,
  });
  const totalRows = usersData?.total || 0;
  const title = pluralize('User', totalRows, true);

  const columnsWithProps = useMemo(
    () =>
      UserColumns.map((col: any) => {
        if (col.field === 'edit') {
          col.onClick = (rowId: string) => {
            setIsUserEditDrawerOpen(true);
            setCurrentUser(rowId);
          };
        }

        return col;
      }),
    [setCurrentUser, setIsUserEditDrawerOpen],
  );

if (isError) {
  const isForbidden = error instanceof ApiError && error.status === 403;
  const status = isForbidden ? '403' : '500';
  const statusMessage = isForbidden ? 'Forbidden' : 'Something went wrong';

  return (
    <CustomErrorPage
      status={status}
      statusMessage={statusMessage}
    />
  );
}

  return (
    <UsersDataGrid
      title={title}
      onRowClick={row => navigate(`${row?.id}`)}
      columns={columnsWithProps}
      // @ts-ignore
      rows={usersData?.elements}
      loading={isLoading || isFetching}
      create="user"
      onPaginationModelChange={handlePagination}
      paginationModel={paginationModel}
      totalRowCount={totalRows}
      isServerSide
      filterModel={filterModel}
      onFilterModelChange={handleFilterModelChange}
      sortModel={sortModel}
      handleSort={handleSortModelChange}
      columnVisibilityModel={columnVisibilityModel}
      onColumnVisibilityModelChange={setColumnVisibilityModel}
    />
  );
}
